import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MdDelete } from 'react-icons/md';
import PageHeader from '../../../components/owner/PageHeader';
import TablePagination from '../../../components/shared/TablePagination';
import { useAuth } from '../../../context/AuthContext';
import { usePanelToast } from '../../../context/PanelToastContext';
import {
  formatCurrency,
  localDateKey,
  mergeCoaTreeWithLiveData,
  buildSubscriptionAccountRows,
  buildCoaSalesEntryRowsFromFinance,
  loadManualCashPayments,
  loadManualBankPayments,
  loadManualCardPayments,
  loadManualQrPayments,
  isCoaCashBankExpenseParent,
} from './financeData';
import {
  fetchCoaTree,
  fetchFinanceLedgerData,
  createCoaLedgerEntryRemote,
  deleteCoaChildRemote,
} from '../../../services/admin/financeLedgerApi';
import { setActiveGymId } from '../../../services/receptionist/gymContext';
import { PAYMENTS_UPDATE_EVENT } from '../../receptionist/paymentStore';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './finance.css';

const LEDGER_PAGE_SIZE = 50;

function emptyEntryForm() {
  return {
    date: localDateKey(),
    description: '',
    debit: '',
    credit: '',
    ref: '',
    paymentMethod: 'cash',
  };
}

function LedgerEntriesTable({ entries, emptyLabel = 'No ledger entries yet.', showPaymentMethod = true }) {
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [entries]);

  const total = entries.length;
  const totalPages = Math.max(1, Math.ceil(total / LEDGER_PAGE_SIZE) || 1);
  const safePage = Math.min(page, totalPages);
  const from = total === 0 ? 0 : (safePage - 1) * LEDGER_PAGE_SIZE + 1;
  const to = Math.min(safePage * LEDGER_PAGE_SIZE, total);
  const pageRows = entries.slice((safePage - 1) * LEDGER_PAGE_SIZE, safePage * LEDGER_PAGE_SIZE);
  const colSpan = showPaymentMethod ? 6 : 5;

  const typeLabel = (row) => {
    const ref = String(row.ref || row.reference || '').trim().toUpperCase();
    if (/^JV\b/.test(ref) || ref.startsWith('JV-') || ref.startsWith('JV_') || /^JV\d/.test(ref)) {
      return 'Ledgers';
    }
    if (row.type === 'Ledgers' || row.type === 'Bank' || row.type === 'Cash') return row.type;
    const raw = String(row.paymentMethod || row.method || '').trim().toLowerCase();
    if (raw === 'ledgers' || raw === 'ledger' || raw === 'journal') return 'Ledgers';
    if (raw === 'bank') return 'Bank';
    if (raw === 'cash') return 'Cash';
    if (/^BPV/.test(ref) || /^BRV/.test(ref)) return 'Bank';
    if (/^CPV/.test(ref) || /^CRV/.test(ref)) return 'Cash';
    return '—';
  };

  return (
    <>
      <div className="owner-table-wrap">
        <table className="owner-table rec-member-table admin-list-table admin-finance-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Ref</th>
              {showPaymentMethod ? <th>Type</th> : null}
              <th>Debit</th>
              <th>Credit</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length ? pageRows.map((row) => (
              <tr key={row.id || `${row.date}-${row.description}-${row.debit}-${row.credit}`}>
                <td>{row.date || '—'}</td>
                <td>{row.description || '—'}</td>
                <td>{row.ref || row.reference || '—'}</td>
                {showPaymentMethod ? (
                  <td>{typeLabel(row)}</td>
                ) : null}
                <td className="finance-amount-negative">
                  {row.debit ? formatCurrency(row.debit) : '—'}
                </td>
                <td className="finance-amount-positive">
                  {row.credit ? formatCurrency(row.credit) : '—'}
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={colSpan} className="owner-placeholder-text" style={{ textAlign: 'center', padding: 16 }}>
                  {emptyLabel}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        page={safePage}
        totalPages={totalPages}
        total={total}
        from={from}
        to={to}
        onPageChange={setPage}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
      />
    </>
  );
}

function CoaLedgerPage() {
  const { parentCode: parentParam, childCode: childParam } = useParams();
  const parentCode = decodeURIComponent(parentParam || '').toUpperCase();
  const childCode = childParam ? decodeURIComponent(childParam).toUpperCase() : '';
  const navigate = useNavigate();
  const toast = usePanelToast();
  const { account } = useAuth();
  const gymId = account?.gym_id || '';

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [entryForm, setEntryForm] = useState(emptyEntryForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!gymId) {
      setTree([]);
      setLoading(false);
      return;
    }
    try {
      setError('');
      setLoading(true);
      setActiveGymId(gymId);
      const [baseTree, finance] = await Promise.all([
        fetchCoaTree(gymId),
        fetchFinanceLedgerData(gymId),
      ]);
      const subscriptionRows = buildSubscriptionAccountRows(finance.payments || []);
      const liveData = {
        cash: [...(subscriptionRows.cash || []), ...loadManualCashPayments(gymId)],
        bank: [...(subscriptionRows.bank || []), ...loadManualBankPayments(gymId)],
        card: [...(subscriptionRows.card || []), ...loadManualCardPayments(gymId)],
        qr: [...(subscriptionRows.qr || []), ...loadManualQrPayments(gymId)],
        salesEntries: buildCoaSalesEntryRowsFromFinance(finance),
      };
      setTree(mergeCoaTreeWithLiveData(baseTree, liveData));
    } catch (err) {
      setError(err.message || 'Failed to load ledger');
      setTree([]);
    } finally {
      setLoading(false);
    }
  }, [gymId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const refresh = () => load();
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
  }, [load]);

  const parent = useMemo(
    () => tree.find((p) => p.code === parentCode) || null,
    [tree, parentCode],
  );

  const child = useMemo(() => {
    if (!childCode || !parent) return null;
    return parent.children.find((c) => c.code === childCode) || null;
  }, [parent, childCode]);

  const mode = childCode ? 'child' : 'parent';
  const showPaymentMethodPicker = isCoaCashBankExpenseParent(parentCode);

  const childLedgerSections = useMemo(() => {
    if (!parent || mode !== 'parent') return [];
    return (parent.children || []).map((c) => {
      const entries = (c.entries || []).map((e) => ({
        ...e,
        childCode: c.code,
        childName: c.name,
      }));
      const sorted = [...entries].sort(
        (a, b) => String(b.date || '').localeCompare(String(a.date || '')),
      );
      const debit = sorted.reduce((s, e) => s + (Number(e.debit) || 0), 0);
      const credit = sorted.reduce((s, e) => s + (Number(e.credit) || 0), 0);
      return {
        child: c,
        entries: sorted,
        debit,
        credit,
      };
    });
  }, [parent, mode]);

  const ledgerEntries = useMemo(() => {
    if (!parent || mode !== 'parent') {
      if (!child) return [];
      return (child.entries || []).map((e) => ({
        ...e,
        childCode: child.code,
        childName: child.name,
      }));
    }
    return [];
  }, [parent, child, mode]);

  const totals = useMemo(() => {
    if (mode === 'child') {
      return {
        debit: ledgerEntries.reduce((s, e) => s + (Number(e.debit) || 0), 0),
        credit: ledgerEntries.reduce((s, e) => s + (Number(e.credit) || 0), 0),
        count: ledgerEntries.length,
      };
    }
    return childLedgerSections.reduce(
      (acc, section) => ({
        debit: acc.debit + section.debit,
        credit: acc.credit + section.credit,
        count: acc.count + section.entries.length,
      }),
      { debit: 0, credit: 0, count: 0 },
    );
  }, [mode, ledgerEntries, childLedgerSections]);

  const title = child
    ? `${child.name} Ledger`
    : parent
      ? `${parent.name} Ledger`
      : 'Account Ledger';

  const subtitle = child
    ? `${parent?.name || parentCode} · ${child.code}`
    : parent
      ? `${parent.code} · all child accounts`
      : parentCode;

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    if (!child || child.isSystem) {
      toast.showError('Manual entries are only for manual child accounts.');
      return;
    }
    try {
      setSaving(true);
      await createCoaLedgerEntryRemote(child.code, {
        ...entryForm,
        debit: Number(entryForm.debit) || 0,
        credit: Number(entryForm.credit) || 0,
        paymentMethod: showPaymentMethodPicker
          ? (entryForm.paymentMethod === 'bank' ? 'bank' : 'cash')
          : undefined,
      }, gymId);
      setShowEntryModal(false);
      setEntryForm(emptyEntryForm());
      toast.showSaved('Ledger entry');
      await load();
    } catch (err) {
      toast.showError(err.message || 'Failed to add ledger entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChild = async () => {
    if (!deleteTarget?.code || deleteTarget.isSystem) return;
    try {
      setDeleting(true);
      await deleteCoaChildRemote(deleteTarget.code, gymId);
      toast.showDeleted(deleteTarget.name || 'Child ledger');
      setDeleteTarget(null);
      if (mode === 'child') {
        navigate(`/admin/finance/coa/${encodeURIComponent(parentCode)}`);
        return;
      }
      await load();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete child ledger');
    } finally {
      setDeleting(false);
    }
  };

  if (!loading && parentCode && !parent) {
    return (
      <div>
        <PageHeader title="Account not found" subtitle={parentCode} />
        <button type="button" className="owner-btn owner-btn-secondary" onClick={() => navigate('/admin/finance')}>
          Back to Finance
        </button>
      </div>
    );
  }

  if (!loading && childCode && parent && !child) {
    return (
      <div>
        <PageHeader title="Child account not found" subtitle={childCode} />
        <button
          type="button"
          className="owner-btn owner-btn-secondary"
          onClick={() => navigate(`/admin/finance/coa/${encodeURIComponent(parentCode)}`)}
        >
          Back to {parent.name}
        </button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        <Link to="/admin/finance" className="owner-btn owner-btn-secondary">
          Back to Chart
        </Link>
        {mode === 'child' && parent && (
          <Link
            to={`/admin/finance/coa/${encodeURIComponent(parent.code)}`}
            className="owner-btn owner-btn-secondary"
          >
            Parent Ledger
          </Link>
        )}
        {child && !child.isSystem && (
          <>
            <button
              type="button"
              className="owner-btn owner-btn-primary"
              onClick={() => {
                setEntryForm(emptyEntryForm());
                setShowEntryModal(true);
              }}
            >
              Add Entry
            </button>
            <button
              type="button"
              className="owner-btn rec-member-btn-delete"
              onClick={() => setDeleteTarget(child)}
            >
              Delete Child
            </button>
          </>
        )}
      </PageHeader>

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading ledger...</p>}
      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--color-danger)' }}>
          {error}
        </p>
      )}

      <div className="owner-card rec-panel-card admin-finance-section">
        <div className="admin-finance-member-header">
          <h2 className="owner-card-title">{title}</h2>
          <p className="owner-placeholder-text" style={{ margin: 0 }}>
            {totals.count} entries
            {' · '}
            Debit {formatCurrency(totals.debit)}
            {' · '}
            Credit {formatCurrency(totals.credit)}
          </p>
        </div>

        {mode === 'parent' ? (
          <div className="admin-finance-coa-child-ledger-stack">
            {childLedgerSections.length ? childLedgerSections.map((section) => (
              <section
                key={section.child.code}
                className="admin-finance-coa-child-ledger-block"
              >
                <div className="admin-finance-coa-child-ledger-head">
                  <div>
                    <Link
                      to={`/admin/finance/coa/${encodeURIComponent(parent.code)}/${encodeURIComponent(section.child.code)}`}
                      className="admin-finance-coa-child-ledger-title"
                    >
                      {section.child.name}
                    </Link>
                    <p className="owner-placeholder-text admin-finance-coa-child-ledger-meta">
                      {section.child.code}
                      {section.child.isSystem ? ' · System account' : ''}
                    </p>
                  </div>
                  <div className="admin-finance-coa-child-ledger-head-actions">
                    <p className="owner-placeholder-text" style={{ margin: 0 }}>
                      {section.entries.length} entries
                      {' · '}
                      Debit {formatCurrency(section.debit)}
                      {' · '}
                      Credit {formatCurrency(section.credit)}
                    </p>
                    {!section.child.isSystem && (
                      <button
                        type="button"
                        className="admin-finance-coa-child-entry-btn admin-finance-coa-child-delete-btn"
                        title="Delete child ledger"
                        aria-label={`Delete ${section.child.name}`}
                        onClick={() => setDeleteTarget(section.child)}
                      >
                        <MdDelete />
                      </button>
                    )}
                  </div>
                </div>
                <LedgerEntriesTable
                  entries={section.entries}
                  emptyLabel="No entries for this child account."
                  showPaymentMethod
                />
              </section>
            )) : (
              <p className="owner-placeholder-text admin-finance-coa-child-ledger-empty">
                No child accounts for this parent.
              </p>
            )}
          </div>
        ) : (
          <LedgerEntriesTable entries={ledgerEntries} showPaymentMethod />
        )}
      </div>

      {showEntryModal && (
        <div className="subscription-modal-overlay" onClick={() => setShowEntryModal(false)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Add Ledger Entry</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setShowEntryModal(false)} aria-label="Close">×</button>
            </div>
            <form className="subscription-form" onSubmit={handleSaveEntry}>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-led-date">Date</label>
                <input
                  id="coa-led-date"
                  type="date"
                  className="owner-form-input"
                  value={entryForm.date}
                  onChange={(e) => setEntryForm((f) => ({ ...f, date: e.target.value }))}
                  required
                />
              </div>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-led-desc">Description</label>
                <input
                  id="coa-led-desc"
                  className="owner-form-input"
                  value={entryForm.description}
                  onChange={(e) => setEntryForm((f) => ({ ...f, description: e.target.value }))}
                  required
                />
              </div>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-led-ref">Ref</label>
                <input
                  id="coa-led-ref"
                  className="owner-form-input"
                  value={entryForm.ref}
                  onChange={(e) => setEntryForm((f) => ({ ...f, ref: e.target.value }))}
                />
              </div>
              {showPaymentMethodPicker ? (
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="coa-led-method">Type</label>
                  <select
                    id="coa-led-method"
                    className="owner-form-input"
                    value={entryForm.paymentMethod || 'cash'}
                    onChange={(e) => setEntryForm((f) => ({
                      ...f,
                      paymentMethod: e.target.value === 'bank' ? 'bank' : 'cash',
                    }))}
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="bank">Bank</option>
                  </select>
                </div>
              ) : null}
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-led-debit">Debit</label>
                <input
                  id="coa-led-debit"
                  type="number"
                  min="0"
                  className="owner-form-input"
                  value={entryForm.debit}
                  onChange={(e) => setEntryForm((f) => ({ ...f, debit: e.target.value, credit: e.target.value ? '' : f.credit }))}
                />
              </div>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-led-credit">Credit</label>
                <input
                  id="coa-led-credit"
                  type="number"
                  min="0"
                  className="owner-form-input"
                  value={entryForm.credit}
                  onChange={(e) => setEntryForm((f) => ({ ...f, credit: e.target.value, debit: e.target.value ? '' : f.debit }))}
                />
              </div>
              <div className="subscription-form-actions">
                <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Entry'}
                </button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setShowEntryModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="subscription-modal-overlay"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            className="subscription-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Child Ledger</h2>
              <button
                type="button"
                className="subscription-modal-close"
                onClick={() => !deleting && setDeleteTarget(null)}
                aria-label="Close"
                disabled={deleting}
              >
                ×
              </button>
            </div>
            <p className="owner-placeholder-text" style={{ marginBottom: 16 }}>
              Delete child ledger <strong>{deleteTarget.name}</strong>
              {deleteTarget.code ? <> ({deleteTarget.code})</> : null}? This removes the child
              account and all of its ledger entries.
            </p>
            <div className="subscription-form-actions">
              <button
                type="button"
                className="owner-btn rec-member-btn-delete"
                onClick={handleDeleteChild}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button
                type="button"
                className="owner-btn owner-btn-secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoaLedgerPage;
